"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";

export default function CarouselContent({
  items = [],
  renderItem,
  className = "",
  showDots = true,
  showDotsFullWidth = false,
  showArrows = false,
  loop = true,
  carouselProps = {},
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    dragFree: true,
    loop: loop,
    containScroll: "trimSnaps",
    ...carouselProps,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <div className="embla relative space-y-[21px] lg:space-y-6">
      {showArrows && (
        <button
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          className="btn-box-shadow absolute top-[40%] -left-5 z-10 m-0 hidden h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white p-[10px] shadow lg:flex"
        >
          <FontAwesomeIcon
            className="h-6 w-6"
            icon={byPrefixAndName.far["chevron-left"]}
          />
        </button>
      )}

      <div className="w-full overflow-hidden" ref={emblaRef}>
        <div className={`flex ${className}`}>
          {items.map((item, index) => (
            <React.Fragment key={item.id ?? index}>
              {renderItem(item, index)}
            </React.Fragment>
          ))}
        </div>
      </div>

      {showArrows && (
        <button
          onClick={scrollNext}
          disabled={!canScrollNext}
          className="absolute top-[40%] -right-5 z-10 m-0 hidden h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white p-[10px] shadow lg:flex"
        >
          <FontAwesomeIcon
            className="h-6 w-6"
            icon={byPrefixAndName.far["chevron-right"]}
          />
        </button>
      )}

      {showDots && (
        <div
          className={`mt-10 justify-center gap-2 ${
            showDotsFullWidth ? "flex" : "hidden lg:flex"
          }`}
        >
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`h-3 rounded-full transition-colors duration-300 ${
                index === selectedIndex ? "bg-primary w-8" : "w-3 bg-gray-300"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
